import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Header, MobileNav } from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  IdentificationCard, 
  Camera, 
  CheckCircle, 
  Clock, 
  XCircle,
  Upload,
  Shield,
  Warning
} from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function VerificationStatusCard({ status, type }) {
  const configs = {
    verified: {
      icon: CheckCircle,
      border: 'border-green-200 bg-green-50 dark:bg-green-950/20',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      titleColor: 'text-green-800 dark:text-green-200',
      textColor: 'text-green-700 dark:text-green-300',
      title: 'Identidade Verificada',
      text: 'Os seus documentos foram verificados com sucesso.'
    },
    pending: {
      icon: Clock,
      border: 'border-yellow-200 bg-yellow-50 dark:bg-yellow-950/20',
      iconBg: 'bg-yellow-100',
      iconColor: 'text-yellow-600',
      titleColor: 'text-yellow-800 dark:text-yellow-200',
      textColor: 'text-yellow-700 dark:text-yellow-300',
      title: 'Em Analise',
      text: 'Os seus documentos estao a ser verificados. Sera notificado quando o processo estiver concluido.'
    },
    rejected: {
      icon: Warning,
      border: 'border-red-200 bg-red-50 dark:bg-red-950/20',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      titleColor: 'text-red-800 dark:text-red-200',
      textColor: 'text-red-700 dark:text-red-300',
      title: 'Verificação Rejeitada',
      text: 'Os documentos submetidos não foram aprovados. Por favor submeta novos documentos.'
    }
  };

  const config = configs[type];
  if (!config) return null;
  const Icon = config.icon;

  return (
    <Card className={`mb-6 ${config.border}`} data-testid={`verification-status-${type}`}>
      <CardContent className="py-6">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-full ${config.iconBg} flex items-center justify-center`}>
            <Icon weight="fill" className={`w-6 h-6 ${config.iconColor}`} />
          </div>
          <div>
            <h3 className={`font-semibold ${config.titleColor}`}>{config.title}</h3>
            <p className={`text-sm ${config.textColor}`}>{config.text}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DocumentUploadField({ label, description, icon: Icon, preview, onFileChange, onRemove, testId }) {
  return (
    <div>
      <label className="text-sm font-medium mb-2 block flex items-center gap-2">
        <Icon className="w-4 h-4" />
        {label}
      </label>
      <p className="text-xs text-muted-foreground mb-3">{description}</p>
      
      <div className="border-2 border-dashed rounded-xl p-6 text-center transition-colors hover:border-primary">
        {preview ? (
          <div className="space-y-4">
            <img src={preview} alt={label} className="max-h-48 mx-auto rounded-lg" />
            <Button variant="outline" size="sm" onClick={onRemove}>
              Remover
            </Button>
          </div>
        ) : (
          <label className="cursor-pointer">
            <Icon className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">Clique para carregar ou arraste o ficheiro</p>
            <p className="text-xs text-muted-foreground mt-1">PNG, JPG ate 10MB</p>
            <Input type="file" accept="image/*" onChange={onFileChange} className="hidden" data-testid={testId} />
          </label>
        )}
      </div>
    </div>
  );
}

export default function VerificationPage() {
  const { user } = useAuth();
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [documentFile, setDocumentFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);

  const fetchVerificationStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/providers/verification-status`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setVerificationStatus(data);
      }
    } catch (error) {
      toast.error('Erro ao carregar estado da verificação');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVerificationStatus();
  }, [fetchVerificationStatus]);

  const handleDocumentChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setDocumentFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setDocumentPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSelfieChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelfieFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setSelfiePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!documentFile || !selfieFile) {
      toast.error('Por favor carregue ambos os documentos');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('document', documentFile);
      formData.append('selfie', selfieFile);

      const response = await fetch(`${API_URL}/providers/verify-identity`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        toast.success('Documentos submetidos com sucesso!');
        fetchVerificationStatus();
        setDocumentFile(null);
        setSelfieFile(null);
        setDocumentPreview(null);
        setSelfiePreview(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao submeter documentos');
      }
    } catch (error) {
      toast.error('Erro ao submeter documentos');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = () => {
    switch (verificationStatus?.verification_status) {
      case 'approved':
        return (
          <Badge className="bg-green-100 text-green-800">
            <CheckCircle weight="fill" className="w-3 h-3 mr-1" />
            Verificado
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-yellow-100 text-yellow-800">
            <Clock weight="fill" className="w-3 h-3 mr-1" />
            Em Análise
          </Badge>
        );
      case 'rejected':
        return (
          <Badge className="bg-red-100 text-red-800">
            <XCircle weight="fill" className="w-3 h-3 mr-1" />
            Rejeitado
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">
            Não Verificado
          </Badge>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-12">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 bg-muted rounded" />
            <div className="h-64 bg-muted rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  const isVerified = verificationStatus?.identity_verified;
  const isPending = verificationStatus?.verification_status === 'pending';
  const isRejected = verificationStatus?.verification_status === 'rejected';

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Header />
      
      <main className="container mx-auto px-4 py-6 max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold font-outfit">Verificação de Identidade</h1>
              <p className="text-muted-foreground">Aumente a confiança dos clientes</p>
            </div>
            {getStatusBadge()}
          </div>

          {/* Benefits */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield weight="duotone" className="w-5 h-5 text-primary" />
                Benefícios da Verificação
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-sm">
                  <CheckCircle weight="fill" className="w-4 h-4 text-primary flex-shrink-0" />
                  Badge de verificado no seu perfil
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <CheckCircle weight="fill" className="w-4 h-4 text-primary flex-shrink-0" />
                  Maior confiança dos clientes
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <CheckCircle weight="fill" className="w-4 h-4 text-primary flex-shrink-0" />
                  Prioridade nas pesquisas
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <CheckCircle weight="fill" className="w-4 h-4 text-primary flex-shrink-0" />
                  Justifique preços premium
                </li>
              </ul>
            </CardContent>
          </Card>

          {isVerified && <VerificationStatusCard type="verified" />}
          {isPending && <VerificationStatusCard type="pending" />}
          {isRejected && <VerificationStatusCard type="rejected" />}

          {/* Upload Form */}
          {(!isVerified && !isPending) && (
            <Card>
              <CardHeader>
                <CardTitle>Submeter Documentos</CardTitle>
                <CardDescription>
                  Carregue um documento de identificação é uma selfie para verificar a sua identidade.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <DocumentUploadField
                  label="Documento de Identificação"
                  description="Cartão de Cidadão, Passaporte ou Carta de Condução"
                  icon={IdentificationCard}
                  preview={documentPreview}
                  onFileChange={handleDocumentChange}
                  onRemove={() => { setDocumentFile(null); setDocumentPreview(null); }}
                  testId="document-input"
                />

                <DocumentUploadField
                  label="Selfie"
                  description="Tire uma selfie segurando o documento junto ao rosto"
                  icon={Camera}
                  preview={selfiePreview}
                  onFileChange={handleSelfieChange}
                  onRemove={() => { setSelfieFile(null); setSelfiePreview(null); }}
                  testId="selfie-input"
                />

                <Button
                  onClick={handleSubmit}
                  disabled={!documentFile || !selfieFile || submitting}
                  className="w-full rounded-full"
                  data-testid="submit-verification-btn"
                >
                  {submitting ? 'A submeter...' : 'Submeter para Verificação'}
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  Os seus documentos são processados de forma segura e apenas utilizados para verificação de identidade.
                </p>
              </CardContent>
            </Card>
          )}
        </motion.div>
      </main>

      <MobileNav />
    </div>
  );
}
